// Definizione colonne della worklist (round 3 punti 1-2): ogni dato ha la
// SUA colonna; ordine/larghezza/visibilità li gestisce useColumnLayout.
// La colonna "azioni" è renderizzata dalla pagina (ha bisogno dei callback).
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatDateIt as formatDateItBase, formatMoney } from "@/shared/format";
import type { Proposal, SortField } from "../types";
import { ChannelCell } from "./ChannelCell";
import { DocTypeBadge } from "./DocTypeBadge";
import { EurekaCheckBadge } from "./EurekaCheckBadge";
import { ReviewReasonCell } from "./ReviewReason";
import { SdiAlertBadge } from "./SdiAlertBadge";
import { AnagraficaBadge, BillingSourceBadge } from "./SourceBadges";
import { StatusBadge } from "./StatusBadge";

/** Stile unico delle azioni di riga: link testuale, spento e senza sottolineatura
 *  quando è disabilitato. Vive qui (con le altre parti condivise della tabella)
 *  perché lo usano sia la pagina sia i componenti di cella. */
export const ACTION_BTN = "text-accent hover:underline disabled:opacity-50 disabled:no-underline";

const TIPI_ANAGRAFICI = { S: "Società", D: "Ditta individuale", P: "Privato", E: "Ente" } as const;

/** Il tipo anagrafico che finisce in Eureka (Davide 16/10: «OM indichi il tipo anagrafico»). */
export function etichettaTipo(r: Pick<Proposal, "is_company" | "tipo_anagrafico">): string {
  if (r.tipo_anagrafico) return TIPI_ANAGRAFICI[r.tipo_anagrafico];
  return r.is_company ? "Azienda" : "Privato";
}

export interface ColumnDef {
  key: string;
  label: string;
  /** Larghezza iniziale px (table-layout fixed: stabile, mai "che balla"). */
  width: number;
  sortField: SortField | null;
  align?: "right";
  render?: (r: Proposal) => ReactNode;
}

function fromRecord(rec: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = (rec ?? {})[key];
  return typeof v === "string" && v ? v : null;
}

/** P.IVA: dal billing (marketplace/manuale) o dall'anagrafica verificata. */
function piva(r: Proposal): string | null {
  return fromRecord(r.billing, "vat_number") ?? fromRecord(r.anagrafica, "piva");
}

// Recapito SdI DICHIARATO dal cliente nell'ordine (`billing.sdi_pec`). Da
// quando scioglie la revisione delle FA IT deve essere visibile: senza, una
// riga verde mostra SDI e PEC vuoti e chi emette non vede il dato che l'ha
// sbloccata. Campo unico: se contiene "@" è una PEC, altrimenti un Cod.Dest.
// «0000000» non si mostra come recapito — è il muto, e stamparlo in colonna
// direbbe il falso proprio dove serve la verità (caso reale: proposta 679).
const sdiMuto = (v: string): boolean => /^0+$/.test(v);

function sdiDichiarato(r: Proposal): string | null {
  const v = fromRecord(r.billing, "sdi_pec");
  return v && !v.includes("@") && !sdiMuto(v) ? v : null;
}

function pecDichiarata(r: Proposal): string | null {
  const v = fromRecord(r.billing, "sdi_pec");
  return v && v.includes("@") ? v : null;
}

/** Nella worklist una data assente si mostra «—», non vuota: una cella bianca
 *  in una riga fiscale non si distingue da un dato non caricato. Il FORMATO è
 *  quello condiviso — prima era il default della locale, che varia per runtime. */
export const formatDateIt = (value: string | null | undefined): string =>
  formatDateItBase(value, "—");

const text = (v: string | null): ReactNode =>
  v ? (
    <span className="block truncate" title={v}>
      {v}
    </span>
  ) : (
    <span className="text-ink-2">—</span>
  );

/** Il numero è stato DEDOTTO dal registro Eureka, non letto da un documento
 *  che abbiamo emesso noi (assign_numbers). La provenienza vive nell'``events``
 *  della proposta: nessuna colonna nuova in DB, e la distinzione resta onesta —
 *  «dedotto» non è «verificato». */
function numeroDedotto(r: Proposal): boolean {
  return (r.events ?? []).some((e) => e.event === "numero_dedotto");
}

export const WORKLIST_COLUMNS: ColumnDef[] = [
  // Casella di selezione: renderizzata dalla pagina (ha bisogno dei callback),
  // come «azioni». Prima colonna e non spostabile di fatto: è l'ancora della
  // riga quando si scorre in orizzontale.
  // La label serve al menu «Colonne» (una voce senza nome sarebbe una riga
  // vuota da spuntare); nell'header al suo posto va la casella «seleziona
  // tutto», quindi non viene mai disegnata in tabella.
  { key: "sel", label: "Selezione", width: 38, sortField: null },
  {
    key: "order_ref",
    label: "Rif.",
    width: 150,
    sortField: null,
    render: (r) => (
      <span className="flex items-center gap-1 truncate">
        {r.order_ref ? (
          <Link
            className="truncate font-display text-[13.5px] font-semibold text-ink underline-offset-2 hover:underline"
            title={r.order_ref}
            to={`/ordini/${encodeURIComponent(r.order_ref)}`}
          >
            {r.order_ref}
          </Link>
        ) : (
          <span className="text-ink-2">manuale</span>
        )}
        {r.partial_shipment ? (
          <span className="text-[11px] text-orange-600" title="Spedizione parziale">
            parz.
          </span>
        ) : null}
      </span>
    ),
  },
  {
    key: "channel",
    label: "Canale",
    width: 150,
    sortField: "channel",
    render: (r) => <ChannelCell channel={r.channel} paymentMethod={r.payment_method} />,
  },
  {
    key: "customer_name",
    label: "Cliente",
    width: 190,
    sortField: "customer_name",
    render: (r) => (
      <span className="block truncate" title={r.customer_name}>
        {r.customer_name}
        <span className="ml-1 text-[11px] text-ink-2">{etichettaTipo(r)}</span>
      </span>
    ),
  },
  {
    key: "fonte",
    label: "Fonte",
    width: 120,
    sortField: null,
    render: (r) => (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <BillingSourceBadge proposal={r} />
        <AnagraficaBadge proposal={r} />
      </span>
    ),
  },
  {
    // Dicitura richiesta in fattura (CUP/PNRR): alert amber ben visibile col
    // testo nel tooltip, cosi' non si dimentica (Davide 15/07).
    key: "invoice_notes",
    label: "Nota",
    width: 120,
    sortField: null,
    render: (r) =>
      r.invoice_notes ? (
        // ⚠ tema: scala amber uccisa dal tailwind extend -> uso orange.
        <span
          className="inline-flex max-w-full items-center gap-1 truncate rounded bg-orange-500/20 px-1.5 py-0.5 text-[11px] font-medium text-orange-700"
          title={r.invoice_notes}
        >
          📝 <span className="truncate">{r.invoice_notes}</span>
        </span>
      ) : (
        <span className="text-ink-2">—</span>
      ),
  },
  {
    key: "ragione_sociale",
    label: "Rag. sociale",
    width: 170,
    sortField: null,
    // Anagrafica verificata, o in sua assenza la ragione sociale dal billing
    // (marketplace/import): senza questo fallback una FA con l'azienda nota nel
    // billing ma non verificata mostrava "—". Stesso pattern della colonna CF.
    render: (r) =>
      text(fromRecord(r.anagrafica, "ragione_sociale") ?? fromRecord(r.billing, "company")),
  },
  { key: "piva", label: "P.IVA", width: 110, sortField: null, render: (r) => text(piva(r)) },
  {
    key: "cf",
    label: "CF",
    width: 135,
    sortField: null,
    render: (r) => text(fromRecord(r.billing, "fiscal_code") ?? fromRecord(r.anagrafica, "cf")),
  },
  {
    key: "sdi",
    label: "SDI",
    width: 90,
    sortField: null,
    // Stesso fallback della colonna CF qui sopra: il recapito dichiarato
    // nell'ordine puo' sciogliere la revisione, quindi deve VEDERSI — una riga
    // verde con la colonna SDI vuota nasconde a chi emette il dato che l'ha
    // sbloccata. `?? null` perche' fromRecord torna undefined, non null.
    render: (r) => text(fromRecord(r.anagrafica, "sdi") ?? sdiDichiarato(r)),
  },
  {
    key: "pec",
    label: "PEC",
    width: 175,
    sortField: null,
    render: (r) => text(fromRecord(r.anagrafica, "pec") ?? pecDichiarata(r)),
  },
  {
    key: "indirizzo",
    label: "Indirizzo",
    width: 180,
    sortField: null,
    render: (r) => text(fromRecord(r.anagrafica, "indirizzo")),
  },
  {
    key: "cap",
    label: "CAP",
    width: 70,
    sortField: null,
    render: (r) => text(fromRecord(r.anagrafica, "cap")),
  },
  {
    key: "citta",
    label: "Città",
    width: 120,
    sortField: null,
    render: (r) => text(fromRecord(r.anagrafica, "citta")),
  },
  {
    key: "provincia",
    label: "Prov.",
    width: 62,
    sortField: null,
    render: (r) => text(fromRecord(r.anagrafica, "provincia")),
  },
  {
    key: "country",
    label: "Paese",
    width: 68,
    sortField: null,
    render: (r) => text(r.country || null),
  },
  {
    key: "order_date",
    label: "Data ordine",
    width: 105,
    sortField: "order_date",
    render: (r) => <>{formatDateIt(r.order_date)}</>,
  },
  {
    key: "invoice_date",
    label: "Data fatt.",
    width: 105,
    sortField: "invoice_date",
    render: (r) => <>{formatDateIt(r.invoice_date)}</>,
  },
  {
    key: "gross_expected",
    label: "Importo",
    width: 95,
    sortField: "gross_expected",
    align: "right",
    // 🔑 Su una nota di credito il documento vale il RIMBORSO, non il lordo
    // dell'ordine (`create_documents._importo_documento`): la colonna deve dire
    // quella cifra, o mostra un numero che non sta su nessun documento. Due
    // casi reali, entrambi in produzione: gli storni aperti a mano avevano
    // `gross_expected` NULL e la cella restava vuota (07/08, ST 448,00 e
    // 718,00), e quattro ST già emesse mostravano il lordo del padre (75,99)
    // al posto dell'importo stornato (62,29).
    render: (r) => {
      const importo =
        r.kind === "credit_note" ? (r.refund_amount ?? r.gross_expected) : r.gross_expected;
      return (
        <span className="tabular-nums">{importo != null ? formatMoney(Number(importo)) : "—"}</span>
      );
    },
  },
  {
    key: "doc_type",
    label: "Sigla",
    width: 85,
    sortField: "doc_type",
    render: (r) => <DocTypeBadge docType={r.doc_type} reviewReason={r.review_reason} />,
  },
  {
    key: "status",
    label: "Stato",
    width: 170,
    sortField: "status",
    // L'alert SdI sta accanto allo stato perché è lo stato che QUALIFICA:
    // «da_inviare» da 17 giorni non è la stessa cosa di «da_inviare» da ieri.
    render: (r) => (
      <span className="inline-flex items-center truncate">
        <StatusBadge status={r.status} />
        <SdiAlertBadge proposal={r} />
      </span>
    ),
  },
  {
    // Perché la riga è ferma, in italiano leggibile: il click apre cosa
    // significa e cosa fare (#5 — «ho dovuto chiedere cosa volesse dire VIES
    // NON valido»). Prima il motivo viveva solo nel tooltip della sigla.
    key: "review_reason",
    label: "Motivo",
    width: 170,
    sortField: null,
    render: (r) => <ReviewReasonCell proposal={r} />,
  },
  {
    key: "document_number",
    label: "N. doc",
    width: 145,
    sortField: "document_number",
    render: (r) => (
      <span className="inline-flex items-center truncate">
        {r.document_number ? (
          <span className="truncate" title={r.document_number}>
            {r.doc_type ?? ""} n.{r.document_number}
          </span>
        ) : (
          <span className="text-ink-2">—</span>
        )}
        {numeroDedotto(r) && (
          <span
            className="ml-1 shrink-0 rounded bg-paper-2 px-1 py-px text-[10px] font-medium text-ink-2"
            title="Numero RICOSTRUITO dal registro Eureka agganciando data, importo e intestatario — non letto da un documento emesso dal portale. Il documento esiste, ma l'abbinamento all'ordine è dedotto."
          >
            dedotto
          </span>
        )}
        <EurekaCheckBadge proposal={r} />
      </span>
    ),
  },
  {
    key: "document_date",
    label: "Data doc",
    width: 100,
    sortField: "document_date",
    render: (r) => <>{formatDateIt(r.document_date)}</>,
  },
  // render dalla pagina (callback azioni): vedi FatturazionePage.
  { key: "azioni", label: "Azioni", width: 230, sortField: null },
];
